import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import Slider from 'rc-slider'
import debounce from 'lodash/debounce'

import { formatPrice } from '../../../../components/utils/pricing'
import styledTheme from '../../../../styles/theme'

const RangeWrapper = styled.div`
  padding-left: 20px;
  margin-bottom: 20px;
`

const LabelWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`

const Label = styled.div`
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  line-height: 1.4;
`

const handleStyle = {
  borderColor: styledTheme.brand.jungle,
  height: 20,
  width: 20,
  marginLeft: -10,
  marginTop: -8,
  backgroundColor: 'white',
}

type TransformCurve = 'logarithmic' | 'linear'

const transformIn = (value: number, curve: TransformCurve) => {
  if (value === 0) {
    return 0
  }

  if (curve === 'logarithmic') {
    return Math.log(value)
  }

  return value
}

const transformOut = (value: number, curve: TransformCurve) => {
  if (value === 0) {
    return 0
  }

  if (curve === 'logarithmic') {
    return Math.exp(value)
  }

  return value
}

type PriceRangeProps = {
  initialPriceStart: number
  initialPriceEnd: number
  minPrice: number
  maxPrice: number
  onChange: (_: [number, number]) => any
  transformCurve?: TransformCurve
}

type Value = [number, number]

export const BasePriceRange = ({
  onChange,
  minPrice,
  maxPrice,
  initialPriceStart,
  initialPriceEnd,
  transformCurve = 'logarithmic',
}: PriceRangeProps) => {
  const [value, setValue] = useState([minPrice, maxPrice])

  useEffect(() => {
    setValue([initialPriceStart, initialPriceEnd])
  }, [initialPriceStart, initialPriceEnd])

  const debouncedOnChange = useMemo(() => debounce(onChange, 200), [onChange])

  const handleOnAfterChange = ([start, end]: Value) => {
    const nextStart = transformOut(start, transformCurve)
    const nextEnd = transformOut(end, transformCurve)

    if (nextStart != null && nextEnd != null) {
      debouncedOnChange([Math.round(nextStart), Math.round(nextEnd)])
    }
  }

  const handleChange = ([start, end]: Value) => {
    const nextStart = transformOut(start, transformCurve)
    const nextEnd = transformOut(end, transformCurve)

    setValue([nextStart, nextEnd])
  }

  return (
    <div>
      <LabelWrapper>
        <Label>{formatPrice(value[0], 0)}</Label>
        <Label>{formatPrice(value[1], 0)}</Label>
      </LabelWrapper>

      {minPrice != null && maxPrice != null && (
        <RangeWrapper>
          <Slider
            range
            trackStyle={[
              {
                backgroundColor: styledTheme?.['-ds-core-400']?.value,
                borderRadius: 0,
              },
            ]}
            railStyle={{
              backgroundColor: styledTheme?.['-ds-neutral-300']?.value,
            }}
            handleStyle={[handleStyle, handleStyle]}
            min={transformIn(minPrice, transformCurve)}
            max={transformIn(maxPrice, transformCurve)}
            step={
              (transformIn(maxPrice, transformCurve) -
                transformIn(minPrice, transformCurve)) /
              1000
            }
            value={[
              transformIn(value[0], transformCurve),
              transformIn(value[1], transformCurve),
            ]}
            onChange={handleChange}
            onAfterChange={handleOnAfterChange}
          />
        </RangeWrapper>
      )}
    </div>
  )
}

export default BasePriceRange
